/**
 * RESPONSIBILITY: write a repeating date down as the text that is stored.
 *
 * One canonical spelling, so two rules that mean the same thing are the same text and a
 * save that changed nothing changes nothing. See repeat-rule.ts for the shape.
 */
import { DAY_CODES, hasTime, type RepeatRule } from './repeat-rule';
import { readRepeat } from './repeat-parse';

/** The canonical text. Reading it back gives the same rule. */
export function formatRepeat(r: RepeatRule): string {
  const out = [`FREQ=${r.freq.toUpperCase()}`];

  if (r.freq === 'weekly' && r.days.length) {
    const days = [...new Set(r.days)].sort((a, b) => a - b);
    out.push(`BYDAY=${days.map((d) => DAY_CODES[d]).join(',')}`);
  }
  // A counted weekday is one day with its count. Without the count it is not a monthly
  // rule anyone can read, so a stray day is dropped rather than written.
  if (r.freq === 'monthly' && r.nth !== undefined && r.days.length) {
    out.push(`BYDAY=${r.nth}${DAY_CODES[r.days[0]]}`);
  }
  if (r.freq === 'yearly' && r.month !== undefined) out.push(`BYMONTH=${r.month}`);
  if (r.freq !== 'weekly' && r.nth === undefined && r.monthDay !== undefined) {
    out.push(`BYMONTHDAY=${r.monthDay}`);
  }
  if (hasTime(r)) out.push(`BYHOUR=${r.hour}`, `BYMINUTE=${r.minute}`);
  return out.join(';');
}

/**
 * The same value spelled the canonical way, or the text untouched when it cannot be read
 * — leaving the refusal to validation, which can say why. Run on every write, so a cron
 * expression saved before the change is stored as a rule the next time anyone saves.
 */
export function canonicalRepeat(text: string): string {
  const r = readRepeat(text);
  return r ? formatRepeat(r) : text;
}
